@if (renderAsDialogContent) {
<div class="modal" data-testid="create-item-modal">
    <div class="modal__header">
      <h2 class="modal__title">Create New {{ itemType() === 'folder' ? 'Folder' : 'Table' }}</h2>
      <button class="modal__close" (click)="onClose()" data-testid="button-close-modal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6 6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>
    
    <div class="modal__body">
      <div class="modal__info">
        <span class="modal__info-label">Parent:</span>
        <span class="modal__info-value">{{ parentName }}</span>
        <span class="modal__depth-badge">{{ depthLabel }}</span>
      </div>
      
      <div class="type-selector">
        <label class="type-selector__label">Select Type</label>
        <div class="type-selector__options">
          <button 
            type="button"
            class="type-card"
            [class.type-card--selected]="itemType() === 'folder'"
            [class.type-card--disabled]="!canCreateFolder"
            [disabled]="!canCreateFolder"
            (click)="selectType('folder')"
            data-testid="button-type-folder"
          >
            <div class="type-card__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
              </svg>
            </div>
            <div class="type-card__content">
              <span class="type-card__title">Folder</span>
              <span class="type-card__desc">Group tables and subfolders</span>
            </div>
            @if (!canCreateFolder) {
              <span class="type-card__limit">Max depth reached</span>
            }
          </button>
          
          <button 
            type="button"
            class="type-card"
            [class.type-card--selected]="itemType() === 'table'"
            [class.type-card--disabled]="!canCreateTable"
            [disabled]="!canCreateTable"
            (click)="selectType('table')"
            data-testid="button-type-table"
          >
            <div class="type-card__icon type-card__icon--table">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 3h18v18H3zM3 9h18M3 15h18M9 3v18"/>
              </svg>
            </div>
            <div class="type-card__content">
              <span class="type-card__title">Table</span>
              <span class="type-card__desc">Data dictionary with fields</span>
            </div>
            @if (!canCreateTable) {
              <span class="type-card__limit">Only at leaf level</span>
            }
          </button>
        </div>
      </div>
      
      <div class="form-group">
        <label class="form-group__label" for="itemName">{{ itemType() === 'folder' ? 'Folder' : 'Table' }} Name</label>
        <input 
          type="text"
          id="itemName"
          class="input"
          [class.input--error]="nameError()"
          [ngModel]="itemName()"
          (ngModelChange)="onItemNameChange($event)"
          (blur)="validateName()"
          placeholder="Enter name..."
          data-testid="input-item-name"
        />
        @if (nameError()) {
          <span class="form-group__error">{{ nameError() }}</span>
        }
        @if (createItemInFlight.apiError()) {
          <span class="form-group__error">{{ createItemInFlight.apiError() }}</span>
        }
      </div>

      @if (itemType() === 'table') {
        <div class="method-selector">
          <label class="form-group__label">Creation Method</label>
          <div class="method-selector__options">
            <button
              type="button"
              class="method-option"
              [class.method-option--selected]="creationMethod() === 'scratch'"
              (click)="selectCreationMethod('scratch')"
              data-testid="button-method-scratch"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span>From scratch</span>
            </button>
            <button
              type="button"
              class="method-option"
              [class.method-option--selected]="creationMethod() === 'csv'"
              (click)="selectCreationMethod('csv')"
              data-testid="button-method-csv"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
              </svg>
              <span>Import from CSV</span>
            </button>
          </div>
        </div>

        @if (creationMethod() === 'scratch') {
          <div class="field-section">
            <h3 class="field-section__title">Initial Field</h3>
            <p class="field-section__hint">Position 1, SubPosition 1. Choose data type.</p>
            <div class="field-grid">
              <div class="form-group">
                <label class="form-group__label" for="fieldName">Field Name <span class="required">*</span></label>
                <input 
                  type="text"
                  id="fieldName"
                  class="input"
                  [class.input--error]="fieldNameError()"
                  [ngModel]="fieldName()"
                  (ngModelChange)="fieldName.set($event)"
                  (blur)="validateFieldName()"
                  placeholder="Enter field name..."
                  data-testid="input-field-name"
                />
                @if (fieldNameError()) {
                  <span class="form-group__error">{{ fieldNameError() }}</span>
                }
              </div>

              <div class="form-group">
                <label class="form-group__label" for="fieldDataType">Data Type</label>
                <select 
                  id="fieldDataType"
                  class="select"
                  [ngModel]="fieldDataTypeId()"
                  (ngModelChange)="fieldDataTypeId.set($event)"
                  data-testid="select-field-datatype"
                >
                  @for (dt of dataTypeOptions(); track dt) {
                    <option [value]="dt.id">{{ dt.code }}</option>
                  }
                </select>
              </div>

              <div class="form-group form-group--wide">
                <label class="form-group__label" for="fieldDescription">Description</label>
                <textarea
                  id="fieldDescription"
                  class="input input--textarea"
                  rows="3"
                  [ngModel]="fieldDescription()"
                  (ngModelChange)="fieldDescription.set($event)"
                  placeholder="Enter field description (optional)"
                  data-testid="input-field-description"
                ></textarea>
              </div>
            </div>
          </div>
        } @else {
          <div class="csv-section">
            <div
              class="dropzone"
              [class.dropzone--active]="isDragging()"
              [class.dropzone--error]="hasError()"
              (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event)"
              (drop)="onDrop($event)"
              data-testid="dropzone"
            >
              @if (selectedFile()) {
                <div class="dropzone__file">
                  <svg class="dropzone__file-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14,2 14,8 20,8" fill="none" stroke="currentColor" stroke-width="2"/>
                  </svg>
                  <span class="dropzone__file-name">{{ selectedFile()?.name }}</span>
                  <button
                    class="dropzone__file-remove"
                    (click)="removeFile()"
                    data-testid="button-remove-file"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M18 6 6 18M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              } @else {
                <div class="dropzone__content">
                  <svg class="dropzone__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                  </svg>
                  <p class="dropzone__text">
                    Drag and drop your CSV file here, or
                    <label class="dropzone__browse">
                      browse
                      <input
                        type="file"
                        accept=".csv"
                        (change)="onFileSelect($event)"
                        data-testid="input-csv-file"
                      />
                    </label>
                  </p>
                  <p class="dropzone__hint">
                    CSV file with columns: Field Name, Data Type, Description
                  </p>
                </div>
              }
            </div>

            @if (validationWarnings().length > 0) {
              <div class="validation-warnings" data-testid="csv-validation-warnings">
                <h4 class="validation-warnings__title">⚠ Warnings</h4>
                <ul class="validation-warnings__list">
                  @for (warning of validationWarnings(); track $index) {
                    <li class="validation-warnings__item">{{ warning }}</li>
                  }
                </ul>
              </div>
            }

            @if (validationErrors().length > 0) {
              <div class="validation-errors" data-testid="csv-validation-errors">
                <h4 class="validation-errors__title">Validation Errors</h4>
                <ul class="validation-errors__list">
                  @for (error of validationErrors(); track $index) {
                    <li class="validation-errors__item">{{ error }}</li>
                  }
                </ul>
              </div>
            }

            @if (previewData().length > 0 && !hasError()) {
              <div class="preview" data-testid="csv-preview">
                <h4 class="preview__title">Preview ({{ previewData().length }} fields)</h4>
                <div class="preview__table-wrapper scrollbar-thin">
                  <table class="preview__table">
                    <thead>
                      <tr>
                        @for (col of previewColumns(); track $index) {
                          <th>{{ col }}</th>
                        }
                      </tr>
                    </thead>
                    <tbody>
                      @for (row of previewData().slice(0, 5); track $index) {
                        <tr>
                          @for (col of previewColumns(); track $index) {
                            <td>{{ row[col] }}</td>
                          }
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
                @if (previewData().length > 5) {
                  <p class="preview__more">... and {{ previewData().length - 5 }} more fields</p>
                }
              </div>
            }
          </div>
        }
      }
    </div>
    
    <div class="modal__footer">
      <button
        type="button"
        class="btn btn--ghost"
        (click)="onClose()"
        [disabled]="createItemInFlight.isInFlight()"
        data-testid="button-cancel"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn btn--primary"
        (click)="onSubmit()"
        [disabled]="isSubmitDisabled()"
        [attr.aria-busy]="createItemInFlight.isInFlight()"
        data-testid="button-create"
      >
        @if (createItemInFlight.isInFlight()) {
          Creating…
        } @else {
          Create {{ itemType() === 'folder' ? 'Folder' : 'Table' }}
        }
      </button>
    </div>
</div>
}
